import { Clock } from 'lucide-react'
import {
  percentageLabel,
  useCommissionHistoryQuery,
  type Affiliation,
  type AffiliateCommissionChange,
} from '@entities/partnership'
import {
  compactCard,
  featureTitle,
  inlineRow,
  muted,
  nodeTitle,
  rowCard,
  sectionHeader,
  tableLike,
} from './product.css'

export function AffiliateCommissionHistory({
  productId,
  affiliations,
}: {
  productId: string
  affiliations: readonly Affiliation[]
}) {
  const history = useCommissionHistoryQuery(productId)

  if (history.isPending || history.isError || !history.data?.length) {
    return null
  }

  return (
    <div className={compactCard}>
      <div className={sectionHeader}>
        <div>
          <span className={inlineRow}>
            <Clock size={16} />
            <span className={featureTitle}>Histórico da comissão</span>
          </span>
          <p className={muted}>
            Alterações do padrão e dos afiliados deste produto. Vendas já feitas não mudam.
          </p>
        </div>
      </div>
      <div className={tableLike}>
        {history.data.map((change) => (
          <div key={change.id} className={rowCard}>
            <div>
              <span className={nodeTitle}>{commissionChangeTitle(change, affiliations)}</span>
              <p className={muted}>{formatCommissionChangeDate(change.createdAt)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function commissionChangeTitle(
  change: AffiliateCommissionChange,
  affiliations: readonly Affiliation[],
) {
  const from = percentageLabel(change.previousPercentage)
  const to = percentageLabel(change.newPercentage)
  if (change.changeSource === 'INDIVIDUAL_OVERRIDE') {
    const affiliate = affiliations.find((item) => item.id === change.agreementId)
    const who = affiliate?.affiliate?.email ?? affiliate?.affiliateId ?? 'afiliado'
    return `${who}: ${from} → ${to}`
  }
  if (change.agreementId) {
    const affiliate = affiliations.find((item) => item.id === change.agreementId)
    const who = affiliate?.affiliate?.email ?? affiliate?.affiliateId ?? 'afiliado'
    return `${who} passou de ${from} para ${to}`
  }
  const affected = change.affectedAgreementsCount ?? 0
  const suffix = affected === 1 ? '1 afiliado atualizado' : `${affected} afiliados atualizados`
  return `Padrão ${from} → ${to} · ${suffix}`
}

function formatCommissionChangeDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
